import React, { useState, useRef, useCallback, useEffect } from "react";

interface CustomSliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  disabled?: boolean;
  className?: string;
  invertTrail?: boolean;
}

const CustomSlider: React.FC<CustomSliderProps> = ({
  min,
  max,
  value,
  onChange,
  step = 1,
  disabled = false,
  className = "",
  invertTrail = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Calculate thumb position as percentage
  const getThumbPosition = useCallback(
    (val: number) => {
      const percentage = ((val - min) / (max - min)) * 100;
      return Math.max(0, Math.min(100, percentage));
    },
    [min, max]
  );

  // Calculate value from mouse position
  const getValueFromPosition = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return localValue;

      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = (clientX - rect.left) / rect.width;
      const rawValue = min + percentage * (max - min);

      // Apply step rounding
      const steppedValue = Math.round(rawValue / step) * step;
      return Math.max(min, Math.min(max, steppedValue));
    },
    [min, max, step, localValue]
  );

  // Handle mouse down on thumb
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);

      console.log("CustomSlider: Mouse down, starting drag");
    },
    [disabled]
  );

  // Handle mouse down on track (jump to position)
  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || isDragging) return;

      e.preventDefault();
      e.stopPropagation();

      const newValue = getValueFromPosition(e.clientX);
      console.log("CustomSlider: Track click, new value:", newValue);

      setLocalValue(newValue);
      onChange(newValue);
    },
    [disabled, isDragging, getValueFromPosition, onChange]
  );

  // Global mouse move handler
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || disabled) return;

      e.preventDefault();

      const newValue = getValueFromPosition(e.clientX);
      console.log("CustomSlider: Dragging, new value:", newValue);

      setLocalValue(newValue);
      onChange(newValue);
    },
    [isDragging, disabled, getValueFromPosition, onChange]
  );

  // Global mouse up handler
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        console.log("CustomSlider: Mouse up, ending drag");
        setIsDragging(false);
      }
    },
    [isDragging]
  );

  // Add/remove global mouse events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "grabbing";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const thumbPosition = getThumbPosition(localValue);

  return (
    <div
      className={`custom-slider-container ${className}`}
      style={{ width: "100%", padding: "10px 0" }}
    >
      {/* Track */}
      <div
        ref={sliderRef}
        className="custom-slider-track"
        onClick={handleTrackClick}
        style={{
          position: "relative",
          width: "100%",
          height: "8px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {/* Thumb */}
        <div
          ref={thumbRef}
          className="custom-slider-thumb"
          onMouseDown={handleMouseDown}
          style={{
            position: "absolute",
            top: "50%",
            left: `${thumbPosition}%`,
            transform: "translate(-50%, -50%)",
            width: "20px",
            height: "20px",
            backgroundColor: disabled ? "#9ca3af" : "#3b82f6",
            border: "2px solid white",
            borderRadius: "50%",
            cursor: disabled ? "not-allowed" : isDragging ? "grabbing" : "grab",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
            transition: isDragging ? "none" : "all 0.1s ease",
            zIndex: 10,
            userSelect: "none",
          }}
        />

        {/* Progress fill */}
        <div
          className="custom-slider-progress"
          style={{
            position: "absolute",
            top: 0,
            ...(invertTrail
              ? { left: "auto", right: "0", width: `${100 - thumbPosition}%` }
              : { left: "0", right: "auto", width: `${thumbPosition}%` }),
            height: "100%",
            backgroundColor: disabled ? "#9ca3af" : "#3b82f6",
            borderRadius: "4px",
            opacity: 0.3,
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
};

export default CustomSlider;
